import { OTEL } from "./attributes";

export const TELEMETRY_SERVICES = [
  "web",
  "api",
  "game-server",
  "preset-server",
  "asset-editor",
  "agent-runner",
  "agent",
  "telemetry-tail",
  "tiles",
  "seed",
] as const;
export type TelemetryService = (typeof TELEMETRY_SERVICES)[number];

export type LogLevel = "debug" | "info" | "warn" | "error";

export const TRACEPARENT_HEADER = "traceparent";

/** The W3C header that carries key-values along with a trace to the next service. */
export const BAGGAGE_HEADER = "baggage";

/** A W3C `baggage` header value holding `entries`. */
export function formatBaggage(entries: Record<string, string>): string {
  return Object.entries(entries)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join(",");
}

/** The entries of a W3C `baggage` header value, without their properties. */
export function readBaggage(value: string | null | undefined): Record<string, string> {
  const entries: Record<string, string> = {};
  for (const member of value?.split(",") ?? []) {
    const [pair = ""] = member.split(";");
    const separator = pair.indexOf("=");
    if (separator <= 0) continue;
    try {
      entries[pair.slice(0, separator).trim()] = decodeURIComponent(
        pair.slice(separator + 1).trim(),
      );
    } catch {}
  }
  return entries;
}

/** A W3C `traceparent` header value: version, trace id, span id, flags. */
export const TRACEPARENT_PATTERN = /^00-([0-9a-f]{32})-([0-9a-f]{16})-([0-9a-f]{2})$/;

export interface SpanContext {
  traceId: string;
  spanId: string;
  sampled: boolean;
}

const allZero = (hex: string) => /^0+$/.test(hex);

function randomHex(bytes: number): string {
  let value: string;
  do {
    const buffer = new Uint8Array(bytes);
    crypto.getRandomValues(buffer);
    value = [...buffer].map(byte => byte.toString(16).padStart(2, "0")).join("");
  } while (allZero(value));
  return value;
}

export const newTraceId = () => randomHex(16);
export const newSpanId = () => randomHex(8);

export function parseTraceparent(value: string | null | undefined): SpanContext | undefined {
  const match = value ? TRACEPARENT_PATTERN.exec(value.trim()) : null;
  if (!match) return;
  const [, traceId, spanId, flags] = match;
  if (!traceId || !spanId || !flags || allZero(traceId) || allZero(spanId)) return;
  return { traceId, spanId, sampled: (Number.parseInt(flags, 16) & 1) === 1 };
}

export function formatTraceparent({ traceId, spanId, sampled }: SpanContext): string {
  return `00-${traceId}-${spanId}-${sampled ? "01" : "00"}`;
}

/** The standard `exception` event for an error. */
export function exceptionEvent(error: unknown, timeMs = Date.now()): SpanEvent {
  const described = describeError(error);
  return {
    name: "exception",
    timeMs,
    attributes: {
      [OTEL.exceptionType]: described.name,
      [OTEL.exceptionMessage]: described.message,
      ...(described.stack ? { [OTEL.exceptionStacktrace]: described.stack } : {}),
    },
  };
}

/** A failure's class, message, and stack, and the SQLSTATE of a database error, such as `42P01`. */
export interface DescribedError {
  name: string;
  message: string;
  stack?: string;
  sqlState?: string;
}

/**
 * What a failure says about itself. An error wrapping a Postgres error, like Drizzle's
 * `Failed query: …`, is described by the Postgres error it wraps: the wrapper only repeats the
 * SQL, which the `db.query` span already holds, and its parameters, which can hold user data. Its
 * stack keeps the wrapper's frames, which show where Pax ran the query, under the Postgres error's
 * headline, so no part of the wrapper's message is recorded.
 */
export function describeError(error: unknown): DescribedError {
  const database = postgresErrorIn(error);
  if (database) {
    const frames = error instanceof Error ? framesOf(error) : undefined;
    return {
      name: database.name,
      message: database.message,
      ...(frames ? { stack: `${database.name}: ${database.message}\n${frames}` } : {}),
      sqlState: database.code,
    };
  }
  return error instanceof Error
    ? { name: error.name, message: error.message, ...(error.stack ? { stack: error.stack } : {}) }
    : { name: typeof error, message: String(error) };
}

/**
 * The frames under an error's headline: its stack without the `name: message` it starts with,
 * which can run over several lines. Nothing, when the stack doesn't start that way.
 */
function framesOf(error: Error): string | undefined {
  const headline = `${error.name}: ${error.message}\n`;
  if (!error.stack?.startsWith(headline)) return undefined;
  return error.stack.slice(headline.length) || undefined;
}

/**
 * A failure's stable identity, the span's `error.type`: a database error's SQLSTATE, as
 * OpenTelemetry's database conventions record it, and otherwise the error's class.
 */
export function errorType(error: unknown): string {
  const described = describeError(error);
  return described.sqlState ?? described.name;
}

function postgresErrorIn(error: unknown): (Error & { code: string }) | undefined {
  const isPostgresError = (candidate: unknown): candidate is Error & { code: string } =>
    candidate instanceof Error &&
    candidate.name === "PostgresError" &&
    typeof (candidate as { code?: unknown }).code === "string";
  if (isPostgresError(error)) return error;
  if (error instanceof Error && isPostgresError(error.cause)) return error.cause;
  return undefined;
}

export type SpanAttribute = string | number | boolean | string[] | number[] | boolean[];
export type SpanAttributes = Record<string, SpanAttribute>;
export type SpanKind = "server" | "client" | "internal" | "consumer" | "producer";

/** The name, kind, and attributes of an invocation's root span. */
export interface InvocationRoot {
  name: string;
  kind: SpanKind;
  attributes: SpanAttributes;
}

/**
 * The root of a run of a Worker's `scheduled` handler, as a FaaS timer invocation. The Worker and
 * telemetry-tail, which rebuilds the root of a run that died, both name it here.
 */
export function scheduledRoot(cron: string): InvocationRoot {
  return {
    name: `scheduled ${cron}`,
    kind: "server",
    attributes: { [OTEL.faasTrigger]: "timer", [OTEL.faasCron]: cron },
  };
}

/**
 * The root of processing a Cloudflare Queues delivery, named as the messaging conventions name a
 * process span. The Worker traces each message; telemetry-tail rebuilds a dead batch.
 */
export function queueRoot(
  queue: string,
  delivery: { messageId: string } | { batchSize: number },
): InvocationRoot {
  return {
    name: `process ${queue}`,
    kind: "consumer",
    attributes: {
      [OTEL.messagingSystem]: "cloudflare_queues",
      [OTEL.messagingOperationType]: "process",
      [OTEL.messagingOperationName]: "process",
      [OTEL.messagingDestinationName]: queue,
      ...("messageId" in delivery
        ? { [OTEL.messagingMessageId]: delivery.messageId }
        : { [OTEL.messagingBatchMessageCount]: delivery.batchSize }),
    },
  };
}

export interface SpanLink {
  traceId: string;
  spanId: string;
  attributes?: SpanAttributes;
}

/** Something that happened at one moment during a span, such as the exception that ended it. */
export interface SpanEvent {
  name: string;
  timeMs: number;
  attributes?: SpanAttributes;
}

/** The key that marks a console line as a finished span. */
export const SPAN_LINE_KEY = "pax.span";

/** One finished span, written by a Worker as a single console line. */
export interface SpanLine {
  service: TelemetryService;
  /** The build that wrote it, filled in by whoever exports it. */
  serviceVersion?: string;
  traceId: string;
  spanId: string;
  parentSpanId?: string;
  name: string;
  kind: SpanKind;
  startMs: number;
  endMs: number;
  status?: { code: "error"; message?: string };
  attributes: SpanAttributes;
  events?: SpanEvent[];
  links?: SpanLink[];
}

/** The key that marks a console line as a product event bound for ClickHouse. */
export const EVENT_LINE_KEY = "pax.event";

/**
 * One notable moment, such as a completed round, written by a Worker as a single console line.
 * The telemetry tail stores it in ClickHouse; its fields are the event's own, kept small.
 */
export interface EventLine {
  eventId: string;
  type: string;
  occurredAt: string;
  [field: string]: unknown;
}

/** One structured log line. Fields other than these are attributes. */
export interface LogLine {
  ts: string;
  level: LogLevel;
  msg: string;
  service: TelemetryService;
  serviceVersion?: string;
  traceId?: string;
  spanId?: string;
  [field: string]: unknown;
}
